import React from 'react';
import { dpsTheme } from '../../../styles/dpsTheme';
import { ScoreSnapshot, ProgressStats } from '../../../services/progressTrackingService';
import { useDeviceSize } from '../../../hooks/useMediaQuery';

interface ScoreEvolutionChartProps {
  snapshots: ScoreSnapshot[];
  stats: ProgressStats | null;
}

export const ScoreEvolutionChart: React.FC<ScoreEvolutionChartProps> = ({ snapshots, stats }) => {
  const { isMobile } = useDeviceSize();
  const initialScore = stats?.initialScore || 0;
  const currentScore = stats?.currentScore || 0;
  const progression = stats?.scoreGain || 0;

  const displaySnapshots = snapshots.slice(-5);
  const maxScore = 100;

  const getBarHeight = (score: number) => {
    return `${(score / maxScore) * 100}%`;
  };

  const getBarClass = (index: number, total: number) => {
    if (index === 0) return 'initial';
    if (index === total - 1) return 'current';
    return 'intermediate';
  };

  const getBarColor = (className: string) => {
    if (className === 'initial') return 'linear-gradient(180deg, #94a3b8, #64748b)';
    if (className === 'current') return 'linear-gradient(180deg, #34d399, #10b981)';
    return 'linear-gradient(180deg, #60a5fa, #3b82f6)';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getSnapshotLabel = (snapshot: ScoreSnapshot) => {
    if (snapshot.snapshot_reason === 'initial') return 'Initial';
    if (snapshot.snapshot_reason === 'action_completed') return 'Action réalisée';
    if (snapshot.snapshot_reason === 'manual_update') return 'Mise à jour manuelle';
    return 'Snapshot';
  };

  return (
    <section style={{
      background: dpsTheme.colors.background.card,
      backdropFilter: 'blur(20px)',
      border: `1px solid ${dpsTheme.colors.border.primary}`,
      borderRadius: dpsTheme.borderRadius.lg,
      padding: isMobile ? dpsTheme.spacing.lg : dpsTheme.spacing['2xl'],
      marginBottom: dpsTheme.spacing['2xl']
    }}>
      <h2 style={{
        fontSize: dpsTheme.typography.fontSize.xl,
        fontWeight: dpsTheme.typography.fontWeight.semibold,
        color: dpsTheme.colors.text.primary,
        marginBottom: dpsTheme.spacing.xl,
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
      }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
          <polyline points="17 6 23 6 23 12"></polyline>
        </svg>
        Évolution du score de sécurité
      </h2>

      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
        gap: dpsTheme.spacing.lg,
        marginBottom: dpsTheme.spacing['2xl']
      }}>
        <div style={{
          background: dpsTheme.colors.background.darker,
          border: `1px solid ${dpsTheme.colors.border.primary}`,
          borderRadius: dpsTheme.borderRadius.md,
          padding: dpsTheme.spacing.xl,
          textAlign: 'center'
        }}>
          <div style={{
            fontSize: dpsTheme.typography.fontSize.xs,
            color: dpsTheme.colors.text.muted,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: '8px'
          }}>
            Score initial
          </div>
          <div style={{
            fontSize: '32px',
            fontWeight: dpsTheme.typography.fontWeight.bold,
            color: dpsTheme.colors.text.primary
          }}>
            {initialScore}
          </div>
        </div>

        <div style={{
          background: dpsTheme.colors.background.darker,
          border: `1px solid ${dpsTheme.colors.border.primary}`,
          borderRadius: dpsTheme.borderRadius.md,
          padding: dpsTheme.spacing.xl,
          textAlign: 'center'
        }}>
          <div style={{
            fontSize: dpsTheme.typography.fontSize.xs,
            color: dpsTheme.colors.text.muted,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: '8px'
          }}>
            Score actuel
          </div>
          <div style={{
            fontSize: '32px',
            fontWeight: dpsTheme.typography.fontWeight.bold,
            color: dpsTheme.colors.text.primary
          }}>
            {currentScore}
          </div>
        </div>

        <div style={{
          background: dpsTheme.colors.background.darker,
          border: `1px solid ${dpsTheme.colors.border.primary}`,
          borderRadius: dpsTheme.borderRadius.md,
          padding: dpsTheme.spacing.xl,
          textAlign: 'center'
        }}>
          <div style={{
            fontSize: dpsTheme.typography.fontSize.xs,
            color: dpsTheme.colors.text.muted,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: '8px'
          }}>
            Progression
          </div>
          <div style={{
            fontSize: '32px',
            fontWeight: dpsTheme.typography.fontWeight.bold,
            color: progression >= 0 ? dpsTheme.colors.accent.green.primary : dpsTheme.colors.priority.high.text
          }}>
            {progression >= 0 ? '+' : ''}{progression}
          </div>
        </div>
      </div>

      <div style={{
        background: dpsTheme.colors.background.darker,
        border: `1px solid ${dpsTheme.colors.border.primary}`,
        borderRadius: dpsTheme.borderRadius.md,
        padding: dpsTheme.spacing.xl,
        marginBottom: dpsTheme.spacing.xl
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          height: '250px',
          gap: '12px',
          padding: '20px 0',
          borderBottom: `2px solid ${dpsTheme.colors.border.primary}`,
          marginBottom: dpsTheme.spacing.lg
        }}>
          {displaySnapshots.length > 0 ? displaySnapshots.map((snapshot, index) => {
            const barClass = getBarClass(index, displaySnapshots.length);
            return (
              <div
                key={snapshot.id}
                style={{
                  flex: 1,
                  height: getBarHeight(snapshot.score),
                  background: getBarColor(barClass),
                  borderRadius: '8px 8px 0 0',
                  position: 'relative',
                  transition: 'all 0.3s',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '0.8';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
              >
                <div style={{
                  position: 'absolute',
                  top: '-24px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  fontSize: dpsTheme.typography.fontSize.sm,
                  fontWeight: dpsTheme.typography.fontWeight.semibold,
                  color: dpsTheme.colors.text.secondary,
                  whiteSpace: 'nowrap'
                }}>
                  {Math.round(snapshot.score)}
                </div>
              </div>
            );
          }) : (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              color: dpsTheme.colors.text.muted,
              fontSize: dpsTheme.typography.fontSize.sm
            }}>
              Aucun snapshot disponible
            </div>
          )}
        </div>

        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: dpsTheme.spacing.xl,
          justifyContent: 'center',
          fontSize: dpsTheme.typography.fontSize.sm,
          color: dpsTheme.colors.text.muted
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '12px',
              height: '12px',
              borderRadius: '3px',
              background: 'linear-gradient(135deg, #94a3b8, #64748b)'
            }} />
            <span>Snapshot initial</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '12px',
              height: '12px',
              borderRadius: '3px',
              background: 'linear-gradient(135deg, #60a5fa, #3b82f6)'
            }} />
            <span>Snapshots intermédiaires</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '12px',
              height: '12px',
              borderRadius: '3px',
              background: 'linear-gradient(135deg, #34d399, #10b981)'
            }} />
            <span>Snapshot actuel</span>
          </div>
        </div>
      </div>

      {displaySnapshots.length > 0 && (
        <div style={{
          background: 'rgba(255, 255, 255, 0.03)',
          borderRadius: dpsTheme.borderRadius.md,
          padding: dpsTheme.spacing.lg
        }}>
          <div style={{
            fontSize: dpsTheme.typography.fontSize.sm,
            fontWeight: dpsTheme.typography.fontWeight.semibold,
            color: dpsTheme.colors.text.muted,
            marginBottom: '12px'
          }}>
            Historique des 5 dernières étapes :
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {displaySnapshots.map((snapshot) => (
              <div
                key={snapshot.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: dpsTheme.typography.fontSize.sm,
                  color: dpsTheme.colors.text.tertiary
                }}
              >
                <span>• {formatDate(snapshot.created_at)} — {getSnapshotLabel(snapshot)}</span>
                <span style={{ fontWeight: dpsTheme.typography.fontWeight.semibold }}>
                  {Math.round(snapshot.score)} pts
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};
